import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Image,
  Linking,
  Animated,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { UserSessionAttributes } from '../models/user-session-model';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { updateUserOnServer } from '../store/actions/user-actions';
import { userActions } from '../store/slices/user-slice';
import RoleService from '../services/role-service';
import { accessibleTextProps } from '../utils/accessibility-utils';

interface Props {
  className?: string;
}

const UserModeSelect: React.FC<Props> = ({ className }) => {
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );
  const [primaryUse, setPrimaryUse] = useState(userSession?.primary_use);
  const driverOpacity = useRef(
    new Animated.Value(userSession?.primary_use === 'driver' ? 1 : 0.5)
  ).current; // Opacity controls for driver button
  const hostOpacity = useRef(
    new Animated.Value(userSession?.primary_use === 'host' ? 1 : 0.5)
  ).current; // Opacity controls for host button
  const tintColor = 'white';
  const dispatch: AppDispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // useEffect(() => {
  //   console.log('userSession?.primary_use =', userSession?.primary_use);
  //   setPrimaryUse(userSession?.primary_use);
  // }, [userSession?.primary_use]);

  if (userSession?.primary_use === 'guest') return null;
  
  // Hide for partners who aren't hosts
  if (RoleService.hasRole(userSession, 'partner') && !RoleService.hasRole(userSession, 'host')) {
    return null;
  }

  const toggleUserMode = async (mode: string) => {
    console.log(`Toggling mode: ${mode}`);
    dispatch(userActions.setIsModeChanging(true));
    if (userSession !== undefined && primaryUse !== mode) {
      console.log('UserSession primary use =', primaryUse);
      setLoading(true);
      // Animate fade effect when switching modes (optional, can keep for feedback)
      Animated.timing(mode === 'driver' ? driverOpacity : hostOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
      Animated.timing(mode === 'driver' ? hostOpacity : driverOpacity, {
        toValue: 0.5,
        duration: 300,
        useNativeDriver: true,
      }).start();
      // Await server call and let Redux update the UI
      setPrimaryUse(mode);
      try {
        await dispatch(
          updateUserOnServer(userSession, { primary_use: mode }, undefined)
        );
      } catch (error) {
        console.error('Error updating user mode:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const openWaitlistWebsite = () => {
    Linking.openURL('https://meanderev.com/waitlist/');
  };

  return (
    <View style={styles.container} className={className}>
      {RoleService.hasRole(userSession, 'user') && 
        !RoleService.hasRole(userSession, 'host') && 
        !RoleService.hasRole(userSession, 'partner') ? (
        <View style={styles.contentContainer}>
          <View style={styles.textContainer}>
            <Text 
              {...accessibleTextProps.label}
              style={styles.text} 
              numberOfLines={2}
            >
              Interested in monetizing your EV charger?
            </Text>
          </View>
          <Pressable
            style={styles.waitlistButton}
            onPress={openWaitlistWebsite}
          >
            <Text 
              {...accessibleTextProps.button}
              style={styles.buttonText}
            >
              JOIN WAITLIST
            </Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.contentContainer}>
          <View style={styles.toggleTextContainer}>
            {loading ? (
              <ActivityIndicator
                size="small"
                color="#fff"
                style={{ marginVertical: 4 }}
              />
            ) : (
              <Text 
                {...accessibleTextProps.label}
                style={styles.textUserMode}
              >
                User Mode:
              </Text>
            )}
          </View>
          <View style={styles.buttonGroup}>
            {/* DRIVER BUTTON */}
            <Animated.View
              style={[styles.animatedButton, { opacity: driverOpacity }]}
            >
              <Pressable
                style={[
                  styles.modeButton,
                  styles.firstModeButton,
                  primaryUse === 'driver' ? null : styles.inactiveButton,
                ]}
                onPress={() => toggleUserMode('driver')}
                disabled={loading}
              >
                <Image
                  source={require('../images/charge-my-car-nav-icon.png')}
                  resizeMode="cover"
                  style={[
                    {
                      height: 22,
                      width: 25,
                      marginRight: 6,
                    },
                    tintColor ? { tintColor } : null,
                  ]}
                />
                <Text 
                  {...accessibleTextProps.button}
                  style={styles.toggleButtonText}
                >
                  DRIVER
                </Text>
              </Pressable>
            </Animated.View>
            {/* HOST BUTTON */}
            <Animated.View
              style={[styles.animatedButton, { opacity: hostOpacity }]}
            >
              <Pressable
                style={[
                  styles.modeButton,
                  styles.secondModeButton,
                  primaryUse === 'host' ? null : styles.inactiveButton,
                ]}
                onPress={() => toggleUserMode('host')}
                disabled={loading}
              >
                <Image
                  source={require('../images/host-nav-icon.png')}
                  resizeMode="cover"
                  style={[
                    {
                      height: 25,
                      width: 20,
                      marginRight: 6,
                    },
                    tintColor ? { tintColor } : null,
                  ]}
                />
                <Text 
                  {...accessibleTextProps.button}
                  style={styles.toggleButtonText}
                >
                  HOST
                </Text>
              </Pressable>
            </Animated.View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginHorizontal: -32, // cancels out 16 pixels on each side
    marginTop: 20,
    minHeight: 64, // Use minHeight to allow growth
    paddingVertical: 8, // Add vertical padding for flexibility
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 0.5,
    borderColor: '#376F78',
    paddingHorizontal: 10,
    alignSelf: 'center',
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'center', // vertically center children
    justifyContent: 'space-between',
    flex: 1, // ensure this container fills the parent's height
  },
  textContainer: {
    flex: 1,
    paddingRight: 40,
    marginLeft: 23,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  toggleTextContainer: {
    flex: 1,
    paddingRight: 15,
    marginLeft: 23,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  text: {
    color: '#FFFFFF',
    fontSize: 14, // Slightly smaller base for better scaling
    flexWrap: 'wrap',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  textUserMode: {
    color: '#FFFFFF',
    fontSize: 15, // Slightly smaller base for better scaling
    flexWrap: 'wrap',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  waitlistButton: {
    backgroundColor: '#40BFB4',
    minHeight: 40, // Use minHeight to allow vertical growth
    minWidth: 120, // Slightly smaller minimum width
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8, // Add vertical padding for flexibility
    marginRight: 23,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 14, // Increased for better readability, adjustsFontSizeToFit handles scaling
    textAlign: 'center',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  toggleButtonText: {
    color: '#FFFFFF',
    fontSize: 14, // Increased for better readability, adjustsFontSizeToFit handles scaling
    textAlign: 'center',
    fontFamily: 'PlusJakartaSans-Regular',
    flexShrink: 1, // Allow text to shrink if needed
  },
  buttonGroup: {
    flex: 3,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 23,
  },
  modeButton: Platform.select({
    web: {
      flex: 2.5,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#40BFB4',
      minHeight: 40, // Use minHeight to allow vertical growth
      borderRadius: 210,
      paddingHorizontal: 8,
      paddingVertical: 8, // Add vertical padding for flexibility
    },
    default: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#40BFB4',
      minHeight: 40, // Use minHeight to allow vertical growth
      borderRadius: 20,
      paddingHorizontal: 8,
      paddingVertical: 8, // Add vertical padding for flexibility
    },
  }),
  inactiveButton: {
    backgroundColor: '#AEAEB2',
  },
  // Apply a margin to create a gap between the buttons.
  firstModeButton: {
    marginRight: 5,
  },
  secondModeButton: {
    marginLeft: 5,
  },
  animatedButton: {
    flex: 1, // Ensures both buttons are equal width
  },
});

export default UserModeSelect;
